import type { AuditEntry } from '@/shared/api/generated/audit';

/* The front page's activity feed, as presentation decisions rather than JSX.
 *
 * The reference shows a timestamped line per change — `10:41  retail.channel — distribution drift`,
 * and `Yesterday  Rule EMI amount is positive activated`. Two decisions carry it, and both are here
 * so they are testable without rendering anything.
 *
 * **The time is relative to the reader's day, not absolute.** A feed of five entries all showing
 * `2026-08-25T10:41:00Z` makes a reader do arithmetic to find out whether anything happened this
 * morning. Today's entries show a clock time; anything older shows how long ago, because "yesterday"
 * is what a person means when they ask when something changed.
 *
 * **The sentence is composed from the entry's own fields and nothing else.** An audit entry carries
 * an actor, an action and an aggregate type; the *snapshot* is deliberately absent from the listing
 * route — no payload leaves PostgreSQL on that path — so a feed cannot say what a change contained,
 * only that it happened, to what kind of thing, and by whom.
 *
 * **That bound is exactly how far the reference art can be met, and the difference is worth stating
 * precisely.** The reference reads `Rule EMI amount is positive activated (2 signatures)` and
 * `dwh-snowflake harvest completed — 504 tables catalogued`. The verb is available — `rule.activated`
 * and `harvest.completed` are the platform's own actions — and the *subject* is not: the rule's name,
 * the source's name and the table count all live in the snapshot, which this route does not select.
 * So the sentences below name what happened and what kind of thing it happened to, and say nothing
 * they cannot support. Inventing "504 tables catalogued" from an entry that carries no count would be
 * the claim-without-mechanism failure this codebase records more often than any other.
 *
 * What this *does* replace is the raw audit vocabulary the feed used to render — `connection.
 * verification_failed on catalog.connection` — which is a wire identifier shown to a steward. The
 * mapping is a copy key per action, and an action with no key falls back to the old rendering rather
 * than to a blank line: a dropped row on an activity feed is a silent gap, and a feed that quietly
 * skips what it does not recognise is one whose completeness nobody can check.
 */

/** Anything older than this many hours renders as a day count rather than a clock time. */
const SAME_DAY_HOURS = 24;

export type ActivityWhen =
  | { readonly kind: 'time'; readonly at: Date }
  | { readonly kind: 'daysAgo'; readonly days: number };

/* When an entry happened, in the form a reader wants.
 *
 * `now` is a parameter rather than `new Date()`, for the reason `HomeScreen`'s `now` already is: a
 * function that reads the clock itself cannot be tested, and a component that calls one re-renders
 * with a different answer on every unrelated state change.
 *
 * The day count is calendar days rather than 24-hour blocks, because a change at 23:00 yesterday is
 * "yesterday" at 08:00 today and "19 hours ago" is a sentence nobody says. Computed by differencing
 * the local date parts, so it respects the reader's own midnight rather than UTC's.
 */
export function activityWhen(occurredAt: string, now: Date): ActivityWhen | null {
  const at = new Date(occurredAt);
  /* An unparseable timestamp yields null rather than an `Invalid Date` rendered as text. The API
   * types this as a date-time string, so this is defence against a shape the contract forbids —
   * which is exactly the case a guard exists for. */
  if (Number.isNaN(at.getTime())) return null;

  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();
  const startOfThatDay = new Date(
    at.getFullYear(),
    at.getMonth(),
    at.getDate(),
  ).getTime();
  const days = Math.round(
    (startOfToday - startOfThatDay) / (SAME_DAY_HOURS * 3600 * 1000),
  );

  /* A future timestamp reads as today rather than as a negative day count. Clock skew between this
   * platform and a browser is real and unbounded, and "in -1 days" is a rendering bug that looks
   * like data — the same call `describeAge` makes for a future incident. */
  if (days <= 0) return { kind: 'time', at };
  return { kind: 'daysAgo', days };
}

/** A stable React key. `chain_date` plus `sequence` is the entry's own composite key — `sequence`
 * restarts at 1 each chain date, so it alone is not unique across the feed. */
export function activityKey(entry: AuditEntry): string {
  return `${entry.chain_date}:${String(entry.sequence)}`;
}

/* ---------------------------------------------------------------- the sentence

 * One copy key per audit action, so the feed reads as English rather than as the wire.
 *
 * **A map keyed on the action string rather than keys interpolated from it.** `t('home.activity.
 * action.' + entry.action)` would be one line and would render the *key* for any action nobody wrote
 * copy for — i18next falls back to the key, so a missing entry shows `home.activity.action.rule.
 * activated` to a steward and every gate stays green. An explicit map makes the absent case
 * detectable (`ACTION_SENTENCES` is enumerable, and a test asserts every value is in the catalogue)
 * and makes the fallback a decision rather than an accident.
 *
 * **The slugs are chosen, not derived.** An action contains dots and i18next's default key separator
 * is a dot, so `home.activity.action.rule.activated` is a key i18next tries to resolve by traversing
 * a nested object that does not exist. Flat camelCase slugs sidestep that entirely.
 *
 * **Every sentence is subject-free, and that is the snapshot bound rather than a style choice.** See
 * the header: the route serves no payload, so "A rule was activated" is the whole of what the entry
 * supports. The actor is rendered separately by the feed, which is why no sentence names one.
 */

/** Actions whose name alone does not say what happened, keyed by `aggregateType:action`.
 *
 * `core.compliance` records bare `register` and `generate` — no namespace — and relies on
 * `aggregate_type` to disambiguate, so those two are the one case where the action string is not a
 * sufficient key. Consulted before `ACTION_SENTENCES` and deliberately kept to the cases that need
 * it: making every lookup composite would mean a new aggregate type silently losing its sentence.
 */
const QUALIFIED_SENTENCES: Readonly<Record<string, string>> = {
  'ComplianceFramework:register': 'home.activity.action.frameworkRegistered',
  'EvidencePack:generate': 'home.activity.action.packGenerated',
};

const ACTION_SENTENCES: Readonly<Record<string, string>> = {
  /* Access and accounts. */
  'access.roles_set': 'home.activity.action.rolesSet',
  'access.starter_roles_refreshed': 'home.activity.action.starterRolesRefreshed',
  'access.user_disabled': 'home.activity.action.userDisabled',
  'access.user_enabled': 'home.activity.action.userEnabled',
  'local_credential.added': 'home.activity.action.credentialAdded',
  'local_credential.restored': 'home.activity.action.credentialRestored',
  'local_credential.deleted': 'home.activity.action.credentialDeleted',
  'local_credential.password_changed': 'home.activity.action.passwordChanged',
  'local_credential.password_reset_requested':
    'home.activity.action.passwordResetRequested',
  'local_credential.password_reset_completed':
    'home.activity.action.passwordResetCompleted',
  'local_credential.throttled': 'home.activity.action.signInThrottled',
  'identity.scim_credential.issued': 'home.activity.action.scimCredentialIssued',
  'identity.scim_credential.revoked': 'home.activity.action.scimCredentialRevoked',
  'scim.group.created': 'home.activity.action.scimGroupCreated',
  'scim.group.members_changed': 'home.activity.action.scimGroupMembersChanged',
  'scim.user.deprovisioned': 'home.activity.action.scimUserDeprovisioned',

  /* Sources and the catalogue. */
  'connection.registered': 'home.activity.action.connectionRegistered',
  'connection.verified': 'home.activity.action.connectionVerified',
  'connection.verification_failed': 'home.activity.action.connectionVerificationFailed',
  'connection.credential_replaced': 'home.activity.action.connectionCredentialReplaced',
  'connection.enabled': 'home.activity.action.connectionEnabled',
  'connection.disabled': 'home.activity.action.connectionDisabled',
  'connection.deleted': 'home.activity.action.connectionDeleted',
  'harvest.requested': 'home.activity.action.harvestRequested',
  'harvest.completed': 'home.activity.action.harvestCompleted',
  'harvest.cancelled': 'home.activity.action.harvestCancelled',
  'profile.requested': 'home.activity.action.profileRequested',
  'classification.approved': 'home.activity.action.classificationApproved',
  'lineage.ingested': 'home.activity.action.lineageIngested',
  'lineage.edge.reviewed': 'home.activity.action.lineageEdgeReviewed',

  /* Rules, runs and their two-eyes transitions. */
  'rule.activated': 'home.activity.action.ruleActivated',
  'rule.deprecated': 'home.activity.action.ruleDeprecated',
  'rule.activate.requested': 'home.activity.action.ruleActivateRequested',
  'rule.activate.approved': 'home.activity.action.ruleActivateApproved',
  'rule.activate.rejected': 'home.activity.action.ruleActivateRejected',
  'rule.deprecate.requested': 'home.activity.action.ruleDeprecateRequested',
  'rule.deprecate.approved': 'home.activity.action.ruleDeprecateApproved',
  'rule.deprecate.rejected': 'home.activity.action.ruleDeprecateRejected',
  'rule_version.published': 'home.activity.action.ruleVersionPublished',
  'binding.created': 'home.activity.action.bindingCreated',
  'binding.disabled': 'home.activity.action.bindingDisabled',
  'rules.binding.bulk_requested': 'home.activity.action.bulkRequested',
  'rules.binding.bulk_applied': 'home.activity.action.bulkApplied',
  'rules.binding.bulk_undo_requested': 'home.activity.action.bulkUndoRequested',
  'rules.binding.bulk_undone': 'home.activity.action.bulkUndone',
  'execution.requested': 'home.activity.action.executionRequested',
  'execution.completed': 'home.activity.action.executionCompleted',
  'execution.cancelled': 'home.activity.action.executionCancelled',
  'task.retried': 'home.activity.action.taskRetried',
  'task.cancelled': 'home.activity.action.taskCancelled',
  'sample.inspected': 'home.activity.action.sampleInspected',
  'sample_policy.configured': 'home.activity.action.samplePolicyConfigured',

  /* Scoring, what-if and reconciliation. */
  'score.computed': 'home.activity.action.scoreComputed',
  'score_model.published': 'home.activity.action.scoreModelPublished',
  'scenario.created': 'home.activity.action.scenarioCreated',
  'scenario.evaluated': 'home.activity.action.scenarioEvaluated',
  'reconciliation.created': 'home.activity.action.reconciliationCreated',
  'reconciliation.tolerances_published':
    'home.activity.action.reconciliationTolerancesPublished',
  'reconciliation.run_requested': 'home.activity.action.reconciliationRunRequested',
  'reconciliation.run_pending': 'home.activity.action.reconciliationRunPending',
  'reconciliation.run_running': 'home.activity.action.reconciliationRunRunning',
  'reconciliation.run_succeeded': 'home.activity.action.reconciliationRunSucceeded',
  'reconciliation.run_failed': 'home.activity.action.reconciliationRunFailed',
  /* The two terminal refusals `RunState` keeps apart from FAILED, because nothing errored — the
   * sketch gate concluded the configuration is wrong and declined the full join. A feed that read
   * either as "failed" would send somebody looking for an outage. */
  'reconciliation.run_keys_disjoint':
    'home.activity.action.reconciliationRunKeysDisjoint',
  'reconciliation.run_keys_not_unique':
    'home.activity.action.reconciliationRunKeysNotUnique',

  /* Contracts, remediation and compliance. */
  'contract.created': 'home.activity.action.contractCreated',
  'contract.submitted': 'home.activity.action.contractSubmitted',
  'contract.signed.producer': 'home.activity.action.contractSignedProducer',
  'contract.signed.consumer': 'home.activity.action.contractSignedConsumer',
  'contract.activated': 'home.activity.action.contractActivated',
  'contract.evaluated': 'home.activity.action.contractEvaluated',
  'contract.retired': 'home.activity.action.contractRetired',
  'simulate.requested': 'home.activity.action.simulateRequested',
  'execute.assess': 'home.activity.action.executeAssess',

  /* Alerting, metering, residency and the platform. */
  'notification_rule.declared': 'home.activity.action.notificationRuleDeclared',
  'notification_rule.withdrawn': 'home.activity.action.notificationRuleWithdrawn',
  'quota.configured': 'home.activity.action.quotaConfigured',
  'rate.configured': 'home.activity.action.rateConfigured',
  'region.registered': 'home.activity.action.regionRegistered',
  'residency.declared': 'home.activity.action.residencyDeclared',
  'residency.placed': 'home.activity.action.residencyPlaced',
  'tenant.provisioned': 'home.activity.action.tenantProvisioned',
  'tenant.suspended': 'home.activity.action.tenantSuspended',
  'tenant.resumed': 'home.activity.action.tenantResumed',

  /* The AI plane. */
  'ai.serving.configured': 'home.activity.action.servingConfigured',
  'ai.serving.completed': 'home.activity.action.servingCompleted',
  'ai.rulegen.generated': 'home.activity.action.rulegenGenerated',
  'ai.anomaly.evaluated': 'home.activity.action.anomalyEvaluated',
  'ai.evals.evaluated': 'home.activity.action.evalsEvaluated',
  'ai.copilot.asked': 'home.activity.action.copilotAsked',
  'ai.autonomous.shadow_started': 'home.activity.action.autonomousShadowStarted',
  'ai.autonomous.evaluated': 'home.activity.action.autonomousEvaluated',
  'ai.autonomous.enable_requested': 'home.activity.action.autonomousEnableRequested',
  'ai.autonomous.enable_decided': 'home.activity.action.autonomousEnableDecided',
  'ai.autonomous.enabled': 'home.activity.action.autonomousEnabled',
  'ai.autonomous.reversed': 'home.activity.action.autonomousReversed',

  /* The plugin registry. */
  'plugin.submitted': 'home.activity.action.pluginSubmitted',
  'plugin.reviewed': 'home.activity.action.pluginReviewed',
  'plugin.published': 'home.activity.action.pluginPublished',
  'plugin.installed': 'home.activity.action.pluginInstalled',
  'plugin.ran': 'home.activity.action.pluginRan',
};

/** Every action this feed can write a sentence for. Exported for the test that holds each one to a
 * catalogue entry — a missing key renders as the key itself, which no other gate can see. */
export const KNOWN_ACTIONS: readonly string[] = [
  ...Object.keys(ACTION_SENTENCES),
  ...Object.keys(QUALIFIED_SENTENCES),
];

/** Every copy key this feed asks for, including the fallback's. Exported for the same test. */
export const SENTENCE_KEYS: readonly string[] = [
  ...Object.values(ACTION_SENTENCES),
  ...Object.values(QUALIFIED_SENTENCES),
];

/** The copy key the fallback renders. Named so the test can assert it exists rather than trusting
 * a literal in two files. */
export const UNRECOGNISED_KEY = 'home.activity.action.unrecognised';

export type ActivitySentence =
  /** A sentence the platform has copy for. `key` takes no arguments. */
  | { readonly kind: 'sentence'; readonly key: string }
  /** No sentence exists for this action, so the raw vocabulary is shown and labelled as raw.
   *
   * **Honest rather than blank, and rendered rather than skipped.** An action added to the platform
   * without a line here is the normal case over time — twenty-one modules write to this chain — and
   * the two wrong answers are dropping the row (a silent gap in a feed whose whole job is
   * completeness) and rendering an empty cell (which reads as a bug in the entry). The wire
   * identifier plus its aggregate type is exactly what the feed used to show for *every* row, so
   * this is the previous behaviour surviving as the floor. */
  | {
      readonly kind: 'unrecognised';
      readonly action: string;
      readonly aggregateType: string;
    };

export function activitySentence(entry: AuditEntry): ActivitySentence {
  const qualified = QUALIFIED_SENTENCES[`${entry.aggregate_type}:${entry.action}`];
  if (qualified !== undefined) return { kind: 'sentence', key: qualified };
  const known = ACTION_SENTENCES[entry.action];
  if (known !== undefined) return { kind: 'sentence', key: known };
  return {
    kind: 'unrecognised',
    action: entry.action,
    aggregateType: entry.aggregate_type,
  };
}
